/**
 * Language Mapping Utilities
 * Maps between language names and ISO codes
 * Matches languageOptions from constants/languageOptions.ts
 */

export const LANGUAGE_NAME_TO_CODE: Record<string, string> = {
  // English variants
  'ENGLISH': 'en-US',
  'ENGLISH (US)': 'en-US',
  'ENGLISH (UK)': 'en-GB',
  'ENGLISH (AUSTRALIA)': 'en-AU',
  'ENGLISH (INDIA)': 'en-IN',

  // Indian regional languages
  'HINDI': 'hi-IN',
  'BENGALI': 'bn-IN',
  'TELUGU': 'te-IN',
  'MARATHI': 'mr-IN',
  'TAMIL': 'ta-IN',
  'GUJARATI': 'gu-IN',
  'KANNADA': 'kn-IN',
  'MALAYALAM': 'ml-IN',
  'PUNJABI': 'pa-IN',
  'URDU': 'ur-IN',

  // International languages
  'SPANISH': 'es-ES',
  'FRENCH': 'fr-FR',
  'GERMAN': 'de-DE',
  'CHINESE': 'zh-CN',
  'JAPANESE': 'ja-JP',
  'KOREAN': 'ko-KR',
  'ARABIC': 'ar-SA',
};

/**
 * All supported language codes (matching languageOptions.ts)
 */
export const SUPPORTED_LANGUAGE_CODES = [
  'en-US', 'en-GB', 'en-AU', 'en-IN',
  'hi-IN', 'bn-IN', 'te-IN', 'mr-IN', 'ta-IN',
  'gu-IN', 'kn-IN', 'ml-IN', 'pa-IN', 'ur-IN',
  'es-ES', 'fr-FR', 'de-DE', 'zh-CN', 'ja-JP', 'ko-KR', 'ar-SA'
];

/**
 * Get language code from language name or code
 * Handles both "ENGLISH" and "en-US" formats
 */
export function getLanguageCode(languageNameOrCode: string): string {
  if (!languageNameOrCode) return 'en-US';

  // If already a valid code (contains hyphen), validate and return
  if (languageNameOrCode.includes('-')) {
    // Validate it's in supported list
    if (SUPPORTED_LANGUAGE_CODES.includes(languageNameOrCode)) {
      return languageNameOrCode;
    }
    // If not in list, default to en-US
    return 'en-US';
  }

  // Convert to uppercase and lookup
  const normalized = languageNameOrCode.toUpperCase().trim();
  return LANGUAGE_NAME_TO_CODE[normalized] || 'en-US';
}

/**
 * Get language name from code
 */
export function getLanguageName(code: string): string {
  const entry = Object.entries(LANGUAGE_NAME_TO_CODE).find(([_, c]) => c === code);
  return entry ? entry[0] : 'ENGLISH';
}

/**
 * Check if language code is supported
 */
export function isSupportedLanguage(code: string): boolean {
  return SUPPORTED_LANGUAGE_CODES.includes(code);
}

/**
 * RTL (Right-to-Left) language codes
 */
export const RTL_LANGUAGE_CODES = ['ar-SA', 'ur-IN'];

/**
 * Check if language is RTL (Right-to-Left)
 */
export function isRTLLanguage(languageCode: string): boolean {
  return RTL_LANGUAGE_CODES.includes(languageCode);
}
